import { Button, Icon } from '@risksmart-app/atomic-ui';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { GetUsersQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Dashboard_Sharing_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { TFunction } from 'i18next';
import _ from 'lodash';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from 'src/components/form/form/FormField';
import Select from 'src/components/form/select';

import { useGetDashboards, useGetUsers } from '@/hooks/queries';
import { useOrgScopedLocalStorage } from '@/hooks/useOrgScopedLocalStorage';

import styles from './style.module.scss';
import type { State as DashboardPreferences } from './useDashboardStore';

type DashboardSortOrder = 'default' | 'alphabetical';

interface Props {
  onChange: (dashboardPreferences: DashboardPreferences) => void;
  selected?: string;
}

// Returns '(shared with you by XY)' if the board was created by someone else from the same org
const getSharingSuffix = (
  t: TFunction<'common', 'dashboard'>,
  sharing: Dashboard_Sharing_Type_Enum | null | undefined,
  createdByUser: null | string | undefined,
  loggedInUserId: string,
  users: GetUsersQuery | undefined
) => {
  if (
    sharing === Dashboard_Sharing_Type_Enum.UserOnly ||
    createdByUser === loggedInUserId ||
    _.isNil(sharing)
  ) {
    return '';
  }

  const userName = users?.user.find(
    (u) => u.Id === createdByUser
  )?.FriendlyName;

  return t('shared_with_you_by', {
    user: userName,
  });
};

export const DashboardSelector: FC<Props> = ({ onChange, selected }) => {
  const { t } = useTranslation('common', { keyPrefix: 'dashboard' });
  const { user } = useRisksmartUser();
  const [sortOrder, setSortOrder] =
    useOrgScopedLocalStorage<DashboardSortOrder>('default', {
      localStorageKey: 'Dashboard-SortPreference',
    });

  const { data: users, refetch: refetchUsers } = useGetUsers({
    queryArgs: {},
  });
  const { data: dashboards, refetch: refetchDashboards } = useGetDashboards({
    queryArgs: {},
  });

  // Refresh queries when dashboard selection changes
  useEffect(() => {
    if (selected) {
      // Refetch both queries to ensure we have the latest data
      refetchDashboards();
      refetchUsers();
    }
  }, [selected, refetchDashboards, refetchUsers]);

  const selectedDashboard = dashboards?.dashboard.find(
    (d) => d.Id === selected
  );

  const selectedOption = {
    value: selected,
    label: `${
      selectedDashboard?.Name ?? t('select_dashboard_placeholder')
    } ${getSharingSuffix(
      t,
      selectedDashboard?.Sharing ?? null,
      selectedDashboard?.CreatedByUser ?? null,
      user!.userId,
      users
    )}`,
  };

  const rawDashboards = dashboards?.dashboard ?? [];
  const sortedDashboards =
    sortOrder === 'alphabetical'
      ? [...rawDashboards].sort((a, b) =>
          a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' })
        )
      : [...rawDashboards].sort((a, b) => {
          const dateA = new Date(
            a.ModifiedAtTimestamp ?? a.CreatedAtTimestamp ?? 0
          ).getTime();
          const dateB = new Date(
            b.ModifiedAtTimestamp ?? b.CreatedAtTimestamp ?? 0
          ).getTime();

          return dateB - dateA;
        });

  const sortedOptions = sortedDashboards.map((d) => ({
    label: `${d.Name} ${getSharingSuffix(
      t,
      d.Sharing,
      d.CreatedByUser,
      user!.userId,
      users
    )}`,
    description: d.Description || '',
    value: d.Id,
  }));

  const hasDashboards = rawDashboards.length > 0;
  const isAlphabetical = sortOrder === 'alphabetical';

  const toggleSort = () => {
    setSortOrder(isAlphabetical ? 'default' : 'alphabetical');
  };

  return (
    <FormField hideLabel disableBottomPadding>
      <div className={'flex items-center gap-2'}>
        <div className={'flex-1 min-w-0'}>
          <Select
            className={`w-full min-w-0 print:hidden ${styles.dashboardSelector}`}
            selectedOption={selectedOption}
            options={sortedOptions}
            onChange={(e) => {
              const dashboardToLoad = dashboards?.dashboard.find(
                (d) => d.Id === e.detail.selectedOption.value
              );
              if (!dashboardToLoad) {
                throw new Error('Dashboard not found');
              }
              try {
                onChange({
                  ...dashboardToLoad.Content,
                  id: dashboardToLoad.Id,
                });
              } catch {
                throw new Error(t('load_dashboard_error'));
              }
            }}
            placeholder={t('select_dashboard_placeholder')}
          />
        </div>
        {hasDashboards && (
          <Button
            variant={isAlphabetical ? 'default' : 'neutral'}
            style={'ghost'}
            size={'icon'}
            onMouseDown={(e: React.MouseEvent) => {
              e.preventDefault();
              toggleSort();
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleSort();
              }
            }}
            title={isAlphabetical ? t('sort_default') : t('sort_alphabetical')}
            aria-label={
              isAlphabetical ? t('sort_default') : t('sort_alphabetical')
            }
            aria-pressed={isAlphabetical}
          >
            <Icon
              name={isAlphabetical ? 'arrow-down-az' : 'filter-lines'}
              size={'sm'}
            />
          </Button>
        )}
      </div>
    </FormField>
  );
};
